import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, FormBuilder, FormArray } from '@angular/forms';

import { Provider } from '../provider';
import { FlashService } from '../../components/flash/flash.service';
import { ProviderService } from '../providers.service';
import { QuicksightReportsRolesService } from '../../reports/quicksight-reports-roles.service';
import { FederatedIdentityGroupService } from '../../federated-identity-groups/federated-identity-group.service';

import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-quicksight-reports',
  templateUrl: './quicksight-reports.component.html',
  styleUrls: ['./quicksight-reports.component.scss'],
  standalone: false
})
export class QuicksightReportComponent implements OnInit {
  provider: Provider;

  quicksightReportForm: FormGroup;
  editingMapping = false;

  // All possible reports
  reportMapping: { id: number; report_name: string; enabled: boolean }[] = [];

  // Join-table rows + currently enabled IDs
  providerReportRows: { id: number; transportation_company_id: number; quicksight_report_id: number }[] = [];
  providerReportIds: number[] = [];

  dispatcherRoleId: number | null = null;

  constructor(
    private flashService: FlashService,
    private providerService: ProviderService,
    private quicksightReportsRolesService: QuicksightReportsRolesService,
    private federatedIdentityGroupService: FederatedIdentityGroupService,
    private fb: FormBuilder,
    private route: ActivatedRoute
  ) {
    this.quicksightReportForm = this.fb.group({
      reports: this.fb.array([])
    });
  }

  get reportsArray(): FormArray {
    return this.quicksightReportForm.get('reports') as FormArray;
  }

  ngOnInit() {
    this.route.data.subscribe((data) => {
      this.provider = data.provider.data;
      this.loadReports();
    });
  }


  private loadReports() {
    // 1) First fetch roles to get dispatcher role ID
    this.federatedIdentityGroupService.getRoles().subscribe({
      next: (rolesResponse) => {
        // Find dispatcher role ID
        const dispatcherRole = rolesResponse.roles?.find(role => role.code?.toLowerCase() === 'dispatcher');
        this.dispatcherRoleId = dispatcherRole?.id || null;

        if (!this.dispatcherRoleId) {
          this.flashService.add({
            type: 'warning',
            message: '<strong>Warning!</strong> Dispatcher role not found.'
          });
          return;
        }

        // 2) Fetch reports filtered by dispatcher role and provider reports in parallel
        forkJoin({
          allReports: this.providerService.getAllReports(this.dispatcherRoleId),
          providerReports: this.providerService.getProviderReports(this.provider.id)
        }).subscribe({
          next: ({ allReports, providerReports }) => {
            // Store provider report rows and IDs
            this.providerReportRows = providerReports || [];
            this.providerReportIds = this.providerReportRows.map(r => r.quicksight_report_id);

            // Build mapping (only dispatcher reports + enabled flag)
            const allReportsData = allReports.quicksight_reports || [];
            this.reportMapping = allReportsData.map(r => ({
              id: r.id,
              report_name: r.report_name,
              enabled: this.providerReportIds.includes(r.id)
            }));

            // Rebuild form from mapping
            this.rebuildForm();
            this.editingMapping = false;
          },
          error: (err) => {
            this.flashService.add({
              type: 'danger',
              message: '<strong>Error!</strong> Failed to load reports.'
            });
            console.error('Error loading reports:', err);
          }
        });
      },
      error: (err) => {
        this.flashService.add({
          type: 'danger',
          message: '<strong>Error!</strong> Failed to load roles.'
        });
        console.error('Error loading roles:', err);
      }
    });
  }

  private rebuildForm() {
    this.reportsArray.clear();

    for (const report of this.reportMapping) {
      this.reportsArray.push(
        new FormGroup({
          value: new FormControl(report.enabled)
        })
      );
    }

    this.disableFormControls();
  }

  editMapping() {
    this.editingMapping = true;
    this.enableFormControls();
  }

  cancelEditing() {
    this.editingMapping = false;
    this.rebuildForm(); 
  }

  update() {
    const formValues = this.reportsArray.value as { value: boolean }[];

    const newEnabledIds = formValues
      .map((row, index) => row.value ? this.reportMapping[index].id : null)
      .filter((id): id is number => id !== null);

    const oldEnabledIds = this.providerReportIds;

    const added = newEnabledIds.filter(id => !oldEnabledIds.includes(id));
    const removed = oldEnabledIds.filter(id => !newEnabledIds.includes(id));

    const rowByReportId = new Map(
      this.providerReportRows.map(r => [r.quicksight_report_id, r.id])
    );

    const addCalls = added.map(id =>
      this.providerService.createProviderReport(this.provider.id, id)
    );

    const deleteCalls = removed.map(id =>
      this.providerService.deleteProviderReport(rowByReportId.get(id) as number)
    );

    if (addCalls.length === 0 && deleteCalls.length === 0) {
      this.editingMapping = false;
      this.disableFormControls();
      return;
    }

    forkJoin([...addCalls, ...deleteCalls]).subscribe({
      next: () => {
        this.flashService.add({
          type: 'success',
          message: '<strong>Success!</strong> Associated Reports have been updated.'
        });

        this.editingMapping = false;
        this.loadReports(); // reload fresh state
      },
      error: () => {
        this.flashService.add({
          type: 'danger',
          message: '<strong>Oops!</strong> There was a problem updating reports.'
        });

        this.editingMapping = false;
        this.loadReports();
      }
    });
  }

  private disableFormControls() {
    this.reportsArray.controls.forEach(control => control.disable());
  }

  private enableFormControls() {
    this.reportsArray.controls.forEach(control => control.enable());
  }
}